import type { FetchOrdersRequest, PurchaseOrder } from "@shared/types";

import { isSameObject } from "../../lib/util";
import { DataTablePagination, DataTableRow } from "../../types/data-table.types";
import {
  PurchaseOrdersActions,
  PurchaseOrdersState,
  SetBatchIdAction,
  SetCustomerAccountNumbersAction,
  SetErrorsAction,
  SetFetchEnabledAction,
  SetFetchingAction,
  SetFullyLoadedAction,
  SetInitializedAction,
  SetLoadingAction,
  SetLoadingKeysAction,
  SetLoadingProgressAction,
  SetPaginationAction,
  SetParamsAction,
  SetPurchaseOrderAction,
  SetPurchaseOrderIdAction,
  SetPurchaseOrdersAction,
  SetPurchaseOrdersCountAction,
  SetShippingDocumentsDialogAction,
  SetSuccessAction,
} from "./types";

export function setInitialized(isInitialized: boolean): SetInitializedAction {
  return {
    type: "SET_INITIALIZED",
    payload: {
      isInitialized,
    },
  };
}

export function setLoading(isLoading: boolean): SetLoadingAction {
  return {
    type: "SET_LOADING",
    payload: { isLoading },
  };
}
export function setLoadingKeys(loadingKeys: { [key: string]: boolean }): SetLoadingKeysAction {
  return {
    type: "SET_LOADING_KEYS",
    payload: { loadingKeys },
  };
}
export function setFetching(isFetching: boolean): SetFetchingAction {
  return {
    type: "SET_FETCHING",
    payload: { isFetching },
  };
}

export function setSuccess(isSuccess: boolean): SetSuccessAction {
  return {
    type: "SET_SUCCESS",
    payload: { isSuccess },
  };
}

export function setErrors(errors: Error[]): SetErrorsAction {
  return {
    type: "SET_ERRORS",
    payload: { errors },
  };
}

export function setPurchaseOrders(purchaseOrders: DataTableRow<PurchaseOrder>[]): SetPurchaseOrdersAction {
  return {
    type: "SET_PURCHASE_ORDERS",
    payload: { purchaseOrders },
  };
}

export function setPurchaseOrder(purchaseOrder?: DataTableRow<PurchaseOrder>): SetPurchaseOrderAction {
  return {
    type: "SET_PURCHASE_ORDER",
    payload: { purchaseOrder },
  };
}
export function setPurchaseOrderId(purchaseOrderId?: string): SetPurchaseOrderIdAction {
  return {
    type: "SET_PURCHASE_ORDER_ID",
    payload: { purchaseOrderId },
  };
}

export function setShippingDocumentsDialog(
  open: boolean,
  purchaseOrder?: PurchaseOrder,
  manifestPdfUrl?: string,
  initialTab?: "shipping-label" | "packing-slip" | "combined" | "manifest"
): SetShippingDocumentsDialogAction {
  return {
    type: "SET_SHIPPING_DOCUMENTS_DIALOG",
    payload: {
      open,
      purchaseOrder,
      manifestPdfUrl,
      initialTab,
    },
  };
}
export function setBatchId(batchId?: string): SetBatchIdAction {
  return {
    type: "SET_BATCH_ID",
    payload: { batchId },
  };
}
export function setCustomerAccountNumbers(customerAccountNumbers?: string): SetCustomerAccountNumbersAction {
  return {
    type: "SET_CUSTOMER_ACCOUNT_NUMBERS",
    payload: { customerAccountNumbers },
  };
}

export function setPurchaseOrdersCount(count: PurchaseOrdersState["count"]): SetPurchaseOrdersCountAction {
  return {
    type: "SET_PURCHASE_ORDERS_COUNT",
    payload: { count },
  };
}

export function setParams(params: FetchOrdersRequest): SetParamsAction {
  return {
    type: "SET_PARAMS",
    payload: { params },
  };
}

export function setPagination(pagination: DataTablePagination): SetPaginationAction {
  return {
    type: "SET_PAGINATION",
    payload: { pagination },
  };
}

export function setLoadingProgress(loadingProgress: PurchaseOrdersState["loadingProgress"]): SetLoadingProgressAction {
  return {
    type: "SET_LOADING_PROGRESS",
    payload: { loadingProgress },
  };
}

export function setFullyLoaded(isFullyLoaded: boolean): SetFullyLoadedAction {
  return {
    type: "SET_FULLY_LOADED",
    payload: { isFullyLoaded },
  };
}

export function setFetchEnabled(isFetchEnabled: boolean): SetFetchEnabledAction {
  return {
    type: "SET_FETCH_ENABLED",
    payload: { isFetchEnabled },
  };
}

function updateIfChanged<K extends keyof PurchaseOrdersState>(
  state: PurchaseOrdersState,
  key: K,
  value: PurchaseOrdersState[K]
): PurchaseOrdersState {
  if (isSameObject(state[key], value)) {
    return state;
  }
  return { ...state, [key]: value };
}

export function reducer(state: PurchaseOrdersState, action: PurchaseOrdersActions): PurchaseOrdersState {
  switch (action.type) {
    case "SET_INITIALIZED":
      return { ...state, isInitialized: action.payload.isInitialized };
    case "SET_LOADING":
      return updateIfChanged(state, "isLoading", action.payload.isLoading);
    case "SET_LOADING_KEYS":
      return updateIfChanged(state, "loadingKeys", action.payload.loadingKeys);
    case "SET_FETCHING":
      return updateIfChanged(state, "isFetching", action.payload.isFetching);
    case "SET_ERRORS":
      return updateIfChanged(state, "errors", action.payload.errors);
    case "SET_PARAMS":
      return updateIfChanged(state, "params", action.payload.params);
    case "SET_PAGINATION":
      return updateIfChanged(state, "pagination", action.payload.pagination);
    case "SET_PURCHASE_ORDERS":
      return updateIfChanged(state, "purchaseOrders", action.payload.purchaseOrders);
    case "SET_PURCHASE_ORDER":
      return updateIfChanged(state, "purchaseOrder", action.payload.purchaseOrder);
    case "SET_PURCHASE_ORDERS_COUNT":
      return updateIfChanged(state, "count", action.payload.count);
    case "SET_PURCHASE_ORDER_ID":
      return { ...state, purchaseOrderId: action.payload.purchaseOrderId };
    case "SET_BATCH_ID":
      return { ...state, batchId: action.payload.batchId };
    case "SET_CUSTOMER_ACCOUNT_NUMBERS":
      return updateIfChanged(state, "customerAccountNumbers", action.payload.customerAccountNumbers);
    case "SET_SHIPPING_DOCUMENTS_DIALOG":
      return {
        ...state,
        shippingDocumentsDialog: {
          open: action.payload.open,
          purchaseOrder: action.payload.purchaseOrder,
          manifestPdfUrl: action.payload.manifestPdfUrl,
          initialTab: action.payload.initialTab,
        },
      };
    case "SET_LOADING_PROGRESS":
      return updateIfChanged(state, "loadingProgress", action.payload.loadingProgress);
    case "SET_FULLY_LOADED":
      return updateIfChanged(state, "isFullyLoaded", action.payload.isFullyLoaded);
    case "SET_FETCH_ENABLED":
      return updateIfChanged(state, "isFetchEnabled", action.payload.isFetchEnabled);
    default:
      return state;
  }
}
